import axios from 'axios';
import { useEffect, useState } from 'react';
import { useMsal } from 'src/auth/firebaseMsalCompat';

import { getApiHost } from '../../utils/getApiHost';
import { loginRequest } from '../../config/authConfig';
import { decodeBase64Utf8 } from '../../utils/decodeBase64Utf8';
import { isProjectNotFoundError } from '../../utils/appErrors';
import { FingeringData } from '../../types/types';
import type { DisplaySettings, Hand, ScoreLayout } from '../../types/types';
import type { GenerationPhase } from '../../model/generationProgress';

interface UseGetProjectPollProps {
  projectId: string;
}

interface ProjectResponse {
  status: string;
  fileXML: string | string[];
  fingeringData: FingeringData;
  hasAnsweredImprovementSurvey?: boolean;
  answeredWaitingSurveyTypes?: string[];
  outputXmlPath?: string;
  name?: string;
  storageFileName?: string | null;
  // Persisted view toggles. Absent / null on legacy projects -> consumers
  // fall back to defaults (pitch off, others on).
  displaySettings?: DisplaySettings | null;
  // What the pre-flight layout probe decided, and the hand the user picked
  // for a single-part score. Both null for grand-staff scores and for
  // projects that predate hand selection.
  scoreLayout?: ScoreLayout | null;
  selectedHand?: Hand | null;
  reportedInstrument?: string | null;
  // 0 for demo generations, 1 when a credit was spent. Null / absent
  // on legacy projects that predate generation metadata.
  creditConsumed?: number | null;
  revision?: number;
  displayRevision?: number;
  // Null unless a job is running. Absent on backends that predate the bar.
  generationPhase?: GenerationPhase | null;
}

const useGetProjectPoll = ({ projectId }: UseGetProjectPollProps) => {
  const { instance } = useMsal();
  const [isPolling, setIsPolling] = useState(false);
  const [fileXML, setFileXML] = useState<string | string[]>([]);
  const [fingering, setFingering] = useState<FingeringData>([]);
  const [currentStatus, setCurrentStatus] = useState<string>('');
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [error, setError] = useState<Error | null>(null);
  // 404: deleted or not on the signed-in account. Expected, so not an `error`.
  const [notFound, setNotFound] = useState(false);
  const [hasAnsweredImprovementSurvey, setHasAnsweredImprovementSurvey] =
    useState<boolean>(false);
  const [answeredWaitingSurveyTypes, setAnsweredWaitingSurveyTypes] = useState<
    string[] | null
  >(null);
  const [outputXmlPath, setOutputXmlPath] = useState<string | undefined>(
    undefined
  );
  const [projectName, setProjectName] = useState<string>('');
  const [storageFileName, setStorageFileName] = useState<string | null>(null);
  const [displaySettings, setDisplaySettings] =
    useState<DisplaySettings | null>(null);
  const [scoreLayout, setScoreLayout] = useState<ScoreLayout | null>(null);
  const [selectedHand, setSelectedHand] = useState<Hand | null>(null);
  const [reportedInstrument, setReportedInstrument] = useState<string | null>(
    null
  );
  const [creditConsumed, setCreditConsumed] = useState<number | null>(null);
  const [revision, setRevision] = useState(0);
  const [displayRevision, setDisplayRevision] = useState(0);
  const [generationPhase, setGenerationPhase] =
    useState<GenerationPhase | null>(null);

  const handleProjectResponse = (projectResponse: ProjectResponse) => {
    const {
      status,
      fileXML: encodedXml,
      fingeringData,
      hasAnsweredImprovementSurvey: surveyAnswered,
      answeredWaitingSurveyTypes: waitingSurveyTypes,
      outputXmlPath: xmlPath,
      name,
      storageFileName: storageName,
      displaySettings: settings,
      scoreLayout: layout,
      selectedHand: hand,
      reportedInstrument: reported,
      creditConsumed: consumed,
      revision: nextRevision,
      displayRevision: nextDisplayRevision,
      generationPhase: phase,
    } = projectResponse;
    setCurrentStatus(status);
    setGenerationPhase(phase ?? null);

    // Needed before the score renders: they decide which side of the staff
    // fingering labels sit on when there is only one staff.
    setScoreLayout(layout ?? null);
    setSelectedHand(hand ?? null);
    setReportedInstrument(reported ?? null);

    // Surface persisted view toggles as soon as they arrive (every poll
    // response carries them once saved; null on legacy projects).
    if (settings !== undefined) {
      setDisplaySettings(settings);
    }

    // Surface the project name as soon as the backend includes it (every
    // poll response carries it, regardless of generation status).
    if (typeof name === 'string') {
      setProjectName(name);
    }

    if (storageName !== undefined) {
      setStorageFileName(storageName ?? null);
    }

    if (consumed !== undefined) {
      setCreditConsumed(consumed ?? null);
    }

    if (typeof nextRevision === 'number') {
      setRevision(nextRevision);
    }
    if (typeof nextDisplayRevision === 'number') {
      setDisplayRevision(nextDisplayRevision);
    }

    // Set survey status
    setHasAnsweredImprovementSurvey(surveyAnswered ?? false);
    setAnsweredWaitingSurveyTypes(waitingSurveyTypes ?? []);

    if (status === 'GENERATION_COMPLETED') {
      setFingering(fingeringData);

      // Set output XML path only when generation is completed
      setOutputXmlPath(xmlPath);

      // Handle both string (XML) and array (PNG blobs) cases
      if (Array.isArray(encodedXml)) {
        // PDF case: array of base64-encoded PNG blobs
        setFileXML(encodedXml);
      } else {
        // XML case: single base64-encoded string
        const decodedXml = decodeBase64Utf8(encodedXml);
        setFileXML(decodedXml);
      }

      setStatusMessage('Fingering generation completed successfully.');
      return true;
    } else if (status === 'GENERATION_FAILED') {
      setStatusMessage(
        'Fingering generation failed. Please try uploading again, or email us for support.'
      );
      return true;
    } else if (status === 'PENDING_GENERATION') {
      setStatusMessage('');
      return false;
    } else if (status === 'AWAITING_HAND_SELECTION') {
      // Nothing is running yet: the score turned out to have a single staff
      // and generation waits on the user telling us which hand it is. Keep
      // polling so the page follows along once they answer -- including when
      // they answer in another tab.
      setStatusMessage(
        'Waiting for you to choose which hand this score is for.'
      );
      return false;
    }
    return false;
  };

  const checkProjectStatus = async () => {
    try {
      const authResult = await instance.acquireTokenSilent({
        ...loginRequest,
      });

      const projectResponse = await axios.get<ProjectResponse>(
        `${getApiHost()}/project/${projectId}`,
        { headers: { Authorization: `Bearer ${authResult.idToken}` } }
      );

      return handleProjectResponse(projectResponse.data);
    } catch (error) {
      if (isProjectNotFoundError(error)) {
        setNotFound(true);
        return true; // Stop polling; retrying can't succeed
      }
      setError(error as Error);
      setStatusMessage(
        'Error loading project. Please try refreshing the page.'
      );
      return true; // Stop polling on error
    }
  };

  const startPolling = async () => {
    if (!projectId || isPolling) return;

    // Reset states on start
    setError(null);
    setIsPolling(true);
    setCurrentStatus('POLLING_STARTED');
    setStatusMessage('Loading your score...');

    try {
      // Make an immediate first check
      const isCompleted = await checkProjectStatus();
      if (isCompleted) {
        setIsPolling(false);
        return;
      }

      // Continue with regular polling if not completed
      const pollInterval = setInterval(async () => {
        try {
          const isCompleted = await checkProjectStatus();
          if (isCompleted) {
            clearInterval(pollInterval);
            setIsPolling(false);
          }
        } catch (error) {
          clearInterval(pollInterval);
          setIsPolling(false);
          setError(error as Error);
          throw error;
        }
      }, 2000);

      // Cleanup function
      return () => {
        clearInterval(pollInterval);
        setIsPolling(false);
      };
    } catch (error) {
      setIsPolling(false);
      setError(error as Error);
      throw error;
    }
  };

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    // Reset states when projectId changes
    setFileXML([]);
    setFingering([]);
    setCurrentStatus('');
    setStatusMessage('');
    setError(null);
    setNotFound(false);
    setOutputXmlPath(undefined);
    setProjectName('');
    setStorageFileName(null);
    setDisplaySettings(null);
    setScoreLayout(null);
    setSelectedHand(null);
    setReportedInstrument(null);
    setCreditConsumed(null);
    setRevision(0);
    setDisplayRevision(0);
    setGenerationPhase(null);

    if (projectId) {
      startPolling().then((cleanupFn) => {
        cleanup = cleanupFn;
      });
    }

    return () => {
      if (cleanup) cleanup();
    };
  }, [projectId]);

  return {
    isPolling,
    fileXML,
    fingering,
    currentStatus,
    statusMessage,
    error,
    notFound,
    hasAnsweredImprovementSurvey,
    answeredWaitingSurveyTypes,
    outputXmlPath,
    projectName,
    storageFileName,
    displaySettings,
    scoreLayout,
    selectedHand,
    reportedInstrument,
    creditConsumed,
    revision,
    displayRevision,
    generationPhase,
    setFingering,
    setRevision,
    setDisplayRevision,
  };
};

export { useGetProjectPoll };
